import {
	ArrowDownLeft,
	ArrowRight,
	ArrowUpRight,
	CreditCard,
	FileClock,
	Landmark,
	ReceiptText,
	WalletCards,
	type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import type { FinancialReconciliationItem } from "@/types/financeiro";

import { FinanceTimeline, FinanceTimelineCard } from "./timeline";
import type { FinanceModal } from "./types";

interface ReconciliationTimelineProps {
	items: FinancialReconciliationItem[];
	page?: number;
	pages?: number;
	onPage?: (page: number) => void;
	onModal: (modal: FinanceModal) => void;
}

export function ReconciliationTimeline({
	items,
	page,
	pages,
	onPage,
	onModal,
}: ReconciliationTimelineProps) {
	return (
		<FinanceTimeline
			items={items}
			dateFor={(item) => item.occurred_on}
			itemKey={(item) => `${item.kind}:${item.id}`}
			renderItem={(item) => <ReconciliationTimelineCard item={item} onModal={onModal} />}
			page={page}
			pages={pages}
			onPage={onPage}
		/>
	);
}

function ReconciliationTimelineCard({
	item,
	onModal,
}: {
	item: FinancialReconciliationItem;
	onModal: (modal: FinanceModal) => void;
}) {
	const Icon = iconFor(item);

	return (
		<FinanceTimelineCard
			icon={Icon}
			title={subjectFor(item)}
			detail={FinanceiroLabels.reconciliation.kind[item.kind]}
			amount={FinanceiroLabels.money(item.amount_cents)}
			actions={
				<Button variant="ghost" size="sm" onClick={() => openReconciliationItem({ item, onModal })}>
					{FinanceiroLabels.reconciliation.action[item.kind]}
					<ArrowRight />
				</Button>
			}
		/>
	);
}

function openReconciliationItem({
	item,
	onModal,
}: {
	item: FinancialReconciliationItem;
	onModal: (modal: FinanceModal) => void;
}) {
	switch (item.kind) {
		case "receipt_bank_missing":
		case "receipt_card_missing":
		case "receipt_evidence_lost":
			onModal({
				kind: "evidence",
				target: { kind: "receipt", id: Number(item.id) },
			});
			return;
		case "bill_bank_missing":
		case "bill_evidence_lost":
			onModal({
				kind: "evidence",
				target: { kind: "bill", id: Number(item.id) },
			});
			return;
		case "bank_income_unmatched":
		case "bank_expense_unmatched":
			onModal({ kind: "bank-resolution", id: Number(item.id) });
			return;
		case "cash_deposit_missing":
		case "cash_deposit_evidence_lost":
			onModal({
				kind: "cash-deposit-evidence",
				id: Number(item.id),
				linked: item.kind === "cash_deposit_evidence_lost",
			});
			return;
		case "card_sale_unmatched":
			onModal({
				kind: "receipt",
				initial: {
					method:
						item.method === "debit_card" || item.method === "other" ? item.method : "credit_card",
					amount_cents: item.amount_cents,
					received_on: item.occurred_on,
					declared_installment_count: item.installment_quantity,
					description: item.description,
					card_sale_id: Number(item.id),
				},
			});
			return;
		case "card_settlement_components_missing":
		case "card_settlement_divergent":
		case "card_settlement_sales_incomplete":
		case "card_settlement_sales_chargeback":
		case "card_settlement_sales_reversed":
		case "card_settlement_sales_unmatched":
		case "card_settlement_unmatched":
		case "card_settlement_evidence_lost":
			onModal({ kind: "settlement", id: item.id });
			return;
		default:
			item.kind satisfies never;
	}
}

function subjectFor(item: FinancialReconciliationItem) {
	if (item.person_name !== null) {
		return item.person_name;
	}

	if (item.description !== null) {
		return item.description;
	}

	return FinanceiroLabels.reconciliation.subject[item.kind];
}

function iconFor(item: FinancialReconciliationItem): LucideIcon {
	if (item.kind.startsWith("receipt")) {
		return ReceiptText;
	}

	if (item.kind.startsWith("bill")) {
		return FileClock;
	}

	if (item.kind.startsWith("card")) {
		return CreditCard;
	}

	if (item.kind.startsWith("cash")) {
		return WalletCards;
	}

	if (item.direction === "expense") {
		return ArrowUpRight;
	}

	if (item.direction === "income") {
		return ArrowDownLeft;
	}

	return Landmark;
}
